import { useEffect, useRef } from 'react';
import type { Branding } from '@adop/shared';

/** The message `type` the storefront preview iframe listens for. */
export const STOREFRONT_PREVIEW_MESSAGE = 'adop:preview';

/** Draft state pushed into the real storefront preview while editing branding. */
export interface StorefrontPreviewPayload {
  name: string;
  about: string | null;
  branding: Branding;
}

export interface StorefrontPreviewMessage {
  type: typeof STOREFRONT_PREVIEW_MESSAGE;
  payload: StorefrontPreviewPayload;
}

/** Build the exact message shape the storefront expects. */
export function storefrontPreviewMessage(payload: StorefrontPreviewPayload): StorefrontPreviewMessage {
  return { type: STOREFRONT_PREVIEW_MESSAGE, payload };
}

/**
 * Posts the current draft form state into the live storefront preview iframe,
 * targeting the preview URL's origin. No-ops (never throws) when the iframe is
 * not mounted or the URL cannot be parsed.
 */
export function postStorefrontPreview(
  iframe: HTMLIFrameElement | null,
  previewUrl: string | null,
  payload: StorefrontPreviewPayload,
): void {
  const target = iframe?.contentWindow;
  if (!target || !previewUrl) return;
  let origin: string;
  try {
    origin = new URL(previewUrl).origin;
  } catch {
    return;
  }
  target.postMessage(storefrontPreviewMessage(payload), origin);
}

/**
 * Trailing debounce for a side effect. Runs `effect` `delayMs` after the last
 * change to `deps`; the latest closure is always the one that runs.
 */
export function useDebouncedEffect(effect: () => void, delayMs: number, deps: unknown[]): void {
  const effectRef = useRef(effect);
  effectRef.current = effect;

  useEffect(() => {
    const timer = window.setTimeout(() => effectRef.current(), delayMs);
    return () => window.clearTimeout(timer);
    // The caller decides which values matter via `deps`.
  }, [delayMs, ...deps]);
}
